import { APPROVE, ASSESSMENTS, CLEAR, COMMON, CONFIGURATION, COPY, CURRENT, DDQ, FOLDERS, IRQ, POTENTIAL_PARENTS, RESTART, REVIEW, SERVICES, START, THIRD_PARTIES, THIRD_PARTIES_BASIC, TOGGLE, TPRM, UNRESTRICTED, VERSIONS } from 'Config/Paths';
import { AssessmentSubjectSummary, SetActiveStatesRequest, UpdateCommonAssessmentRequest } from 'Models/AssessmentSubject';
import { SaveOrSubmitFinalReviewRequest, StartAssessmentWorkflowRequest } from 'Models/DueDiligenceWorkflow';
import { CreateOrUpdateFolderRequest, FolderAllVersions, FolderTypes, FolderVersion } from 'Models/Folders';
import { InherentRiskQuestionnaireConfiguration, InherentRiskQuestionnaireResponse } from 'Models/InherentRisk';
import { ProductModule } from 'Models/ProductModule';
import { ResponseModel } from 'Models/ResponseModel';
import { CompleteTprmIntakeRequest, CreateServiceRequest, CreateThirdPartyRequest, ServiceForNonTprmUser, ThirdParty, ThirdPartyForNonTprmUser, ThirdPartyResponse, ThirdPartyResponseWithServices, ThirdPartyService, ThirdPartyWithServicesForNonTprmUser, TprmSaveOrSubmitInherentRiskQuestionnaireRequest, UpdateServiceRequest, UpdateThirdPartyRequest } from 'Models/TPRM';
import { restApiDelete, restApiGet, restApiPost } from 'RestApiConnector';

import { ClientTPRMApi } from './TPRMApi';

export class AWSTPRMApi implements ClientTPRMApi {
    readonly module = ProductModule.TPRM;

    // Third Parties
    createThirdParty = (createThirdPartyRequest: CreateThirdPartyRequest): Promise<ResponseModel<ThirdPartyResponse | ThirdPartyForNonTprmUser>> => {
        return restApiPost(`/${TPRM}/${THIRD_PARTIES}`, createThirdPartyRequest);
    };

    deleteThirdParty = (thirdPartyId: string): Promise<ResponseModel<void>> => {
        return restApiDelete(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}`);
    };

    getAllThirdPartiesWithBasicData = (): Promise<ResponseModel<ThirdParty[]>> => {
        return restApiGet(`/${TPRM}/${THIRD_PARTIES_BASIC}`, {});
    };

    /** @returns A list of all Third Parties and their Services. The list is sorted by Third-Party name. */
    getThirdParties = (): Promise<ResponseModel<ThirdPartyResponseWithServices[]>> => {
        return restApiGet(`/${TPRM}/${THIRD_PARTIES}`, {}).then((response): ResponseModel<ThirdPartyResponseWithServices[]> => {
            return {
                ...response,
                data: response.data.sort((thirdPartyA: ThirdPartyResponseWithServices, thirdPartyB: ThirdPartyResponseWithServices) => thirdPartyA.name.localeCompare(thirdPartyB.name)),
            };
        });
    };

    getThirdPartiesForNonTprmUser = (): Promise<ResponseModel<ThirdPartyWithServicesForNonTprmUser[]>> => {
        return restApiGet(`/${TPRM}/${UNRESTRICTED}/${THIRD_PARTIES}`, {});
    };

    getThirdPartyDetails = (thirdPartyId: string): Promise<ResponseModel<ThirdPartyResponse>> => {
        return restApiGet(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}`, {});
    };

    getThirdPartyDetailsForNonTprmUser = (thirdPartyId: string): Promise<ResponseModel<ThirdPartyForNonTprmUser>> => {
        return restApiGet(`/${TPRM}/${UNRESTRICTED}/${THIRD_PARTIES}/${thirdPartyId}`, {});
    };

    updateThirdParty = (updateThirdPartyRequest: UpdateThirdPartyRequest, thirdPartyId: string): Promise<ResponseModel<ThirdPartyResponse | ThirdPartyForNonTprmUser>> => {
        return restApiPost(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}`, updateThirdPartyRequest);
    };

    completeTprmIntake = (request: CompleteTprmIntakeRequest): Promise<ResponseModel<void>> => {
        return restApiPost(`/${TPRM}/${APPROVE}`, request);
    };

    setActiveStates = (request: SetActiveStatesRequest, thirdPartyId: string): Promise<ResponseModel<void>> => {
        return restApiPost(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${TOGGLE}`, request);
    };

    // Third-Party Services
    createThirdPartyService = (request: CreateServiceRequest, thirdPartyId: string): Promise<ResponseModel<ThirdPartyService | ServiceForNonTprmUser>> => {
        return restApiPost(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${SERVICES}`, request);
    };

    deleteService = (thirdPartyId: string, serviceId: string): Promise<ResponseModel<void>> => {
        return restApiDelete(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${SERVICES}/${serviceId}`);
    };

    getServiceDetails = (vendor_id: string, service_id: string): Promise<ResponseModel<ThirdPartyService>> => {
        return restApiGet(`/${TPRM}/${THIRD_PARTIES}/${vendor_id}/${SERVICES}/${service_id}`);
    };

    getServiceDetailsForNonTprmUser = (vendor_id: string, service_id: string): Promise<ResponseModel<ServiceForNonTprmUser>> => {
        return restApiGet(`/${TPRM}/${UNRESTRICTED}/${THIRD_PARTIES}/${vendor_id}/${SERVICES}/${service_id}`);
    };

    /** Get Third-Party Services for a specific Third Party or all Third-Party Services if no Third Party is specified. */
    getThirdPartyServices = (thirdPartyId?: string): Promise<ResponseModel<ThirdPartyService[]>> => {
        if (thirdPartyId) {
            return restApiGet(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${SERVICES}`);
        } else {
            return restApiGet(`/${TPRM}/${THIRD_PARTIES}/${SERVICES}`);
        }
    };

    copyAssessmentReviewToFinalReview = (thirdPartyId: string, serviceId: string): Promise<ResponseModel<ThirdPartyService>> => {
        return restApiPost(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${SERVICES}/${serviceId}/${REVIEW}/${COPY}`, {});
    };

    saveOrSubmitFinalReview = (thirdPartyId: string, serviceId: string, request: SaveOrSubmitFinalReviewRequest): Promise<ResponseModel<ThirdPartyService>> => {
        return restApiPost(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${SERVICES}/${serviceId}/${REVIEW}`, request);
    };

    updateCommonAssessment = (request: UpdateCommonAssessmentRequest): Promise<ResponseModel<void>> => {
        return restApiPost(`/${TPRM}/${ASSESSMENTS}/${COMMON}`, request);
    };

    updateThirdPartyService = (request: UpdateServiceRequest, thirdPartyId: string, serviceId: string): Promise<ResponseModel<ThirdPartyService | ServiceForNonTprmUser>> => {
        return restApiPost(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${SERVICES}/${serviceId}`, request);
    };

    // Workflow
    startNewAssessmentWorkflow = (thirdPartyId: string, serviceId: string, request: StartAssessmentWorkflowRequest): Promise<ResponseModel<void>> => {
        return restApiPost(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${SERVICES}/${serviceId}/${ASSESSMENTS}/${START}`, request);
    };

    restartAssessmentWorkflow = (thirdPartyId: string, serviceId: string, request: StartAssessmentWorkflowRequest): Promise<ResponseModel<void>> => {
        return restApiPost(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${SERVICES}/${serviceId}/${ASSESSMENTS}/${RESTART}`, request);
    };

    clearAssessmentWorkflow = (thirdPartyId: string, serviceId: string): Promise<ResponseModel<void>> => {
        return restApiPost(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${SERVICES}/${serviceId}/${ASSESSMENTS}/${CLEAR}`, {});
    };

    // IRQ
    getInherentRiskQuestionnaireConfiguration = (): Promise<ResponseModel<InherentRiskQuestionnaireConfiguration>> => {
        return restApiGet(`/${TPRM}/${IRQ}/${CONFIGURATION}`);
    };

    setInherentRiskQuestionnaireConfiguration = (configuration: InherentRiskQuestionnaireConfiguration): Promise<ResponseModel<void>> => {
        return restApiPost(`/${TPRM}/${IRQ}/${CONFIGURATION}`, configuration);
    };

    getInherentRiskQuestionnaire = (vendor_id: string, service_id: string): Promise<ResponseModel<InherentRiskQuestionnaireResponse | undefined>> => {
        return restApiGet(`/${TPRM}/${THIRD_PARTIES}/${vendor_id}/${SERVICES}/${service_id}/${IRQ}`);
    };

    saveInherentRiskQuestionnaire = (vendor_id: string, service_id: string, request: TprmSaveOrSubmitInherentRiskQuestionnaireRequest): Promise<ResponseModel<InherentRiskQuestionnaireResponse>> => {
        return restApiPost(`/${TPRM}/${THIRD_PARTIES}/${vendor_id}/${SERVICES}/${service_id}/${IRQ}`, request);
    };

    getCommonAssessmentPotentialParents = (thirdPartyId: string, serviceId: string): Promise<ResponseModel<AssessmentSubjectSummary[]>> => {
        return restApiGet(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${SERVICES}/${serviceId}/${DDQ}/${POTENTIAL_PARENTS}/`);
    };

    //TPRM Documents/Folders
    createNewFolder = (thirdPartyId: string, createNewFolderRequest: CreateOrUpdateFolderRequest): Promise<ResponseModel<string>> => {
        return restApiPost(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${FOLDERS}`, createNewFolderRequest);
    };

    createNewFolderVersion = (thirdPartyId: string, folderId: string, createNewFolderVersionRequest: CreateOrUpdateFolderRequest): Promise<ResponseModel<void>> => {
        return restApiPost(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${FOLDERS}/${folderId}/${VERSIONS}`, createNewFolderVersionRequest);
    };

    deleteFolder = (thirdPartyId: string, folderId: string): Promise<ResponseModel<void>> => {
        return restApiDelete(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${FOLDERS}/${folderId}`);
    };

    getFolderTypes = (): Promise<ResponseModel<FolderTypes>> => {
        return restApiGet(`/${TPRM}/${FOLDERS}/${CONFIGURATION}`, {});
    };

    /** Get all current Folder versions for all Third Parties. */
    getAllCurrentFolders = (): Promise<ResponseModel<FolderVersion[]>> => {
        return restApiGet(`/${TPRM}/${FOLDERS}`, {});
    };

    getAllCurrentFoldersForThirdParty = (thirdPartyId: string): Promise<ResponseModel<FolderVersion[]>> => {
        return restApiGet(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${FOLDERS}`, {});
    };

    getAllFolderVersions = (thirdPartyId: string, folderId: string): Promise<ResponseModel<FolderAllVersions>> => {
        return restApiGet(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${FOLDERS}/${folderId}`);
    };

    manageFolder = (thirdPartyId: string, folderId: string, createNewFolderVersionRequest: CreateOrUpdateFolderRequest): Promise<ResponseModel<void>> => {
        return restApiPost(`/${TPRM}/${THIRD_PARTIES}/${thirdPartyId}/${FOLDERS}/${folderId}/${VERSIONS}/${CURRENT}`, createNewFolderVersionRequest);
    };

    updateFolderTypes = (folderTypes: FolderTypes): Promise<ResponseModel<void>> => {
        return restApiPost(`/${TPRM}/${FOLDERS}/${CONFIGURATION}`, folderTypes);
    };
}
